import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  ui$,
  setViewMode,
  setSortBy,
  setShowTranslatedOnly,
  setShowAudioOnly,
  setHideSharedArticles,
  setActiveTab,
  isListShared,
} from '../../store';
import { Button } from '../../design-system';
import Sheet from './Sheet';

const VIEW_MODES = ['normal', 'compact', 'list', 'card'];

const SORT_OPTIONS = [
  { value: 'created_at', labelKey: 'sort.addTime' },
  { value: 'last_viewed_at', labelKey: 'sort.lastViewed' },
  { value: 'progress', labelKey: 'sort.progress' },
  { value: 'rating', labelKey: 'sort.starRating' },
];

const Chip = ({ selected, onClick, grow, children }) => (
  <Button
    variant={selected ? 'selected' : 'ghost'}
    size="lg"
    className={grow ? 'mobile-sheet-chip--grow' : undefined}
    aria-pressed={selected}
    onClick={onClick}
  >
    {children}
  </Button>
);

/**
 * View & sort. The desktop header scatters layout, sort, and filter across
 * three controls; at phone width they collapse into one sheet behind a single
 * icon, with the same options and the same store.
 */
const ViewSortSheet = ({ isOpen, onClose, listId }) => {
  const { t } = useTranslation('articles');
  const { pathname } = useLocation();

  const viewMode = useSelector(() => ui$.viewMode.get());
  const sortBy = useSelector(() => ui$.sortBy.get());
  const showTranslatedOnly = useSelector(() => ui$.showTranslatedOnly.get());
  const showAudioOnly = useSelector(() => ui$.showAudioOnly.get());
  const hideSharedArticles = useSelector(() => ui$.hideSharedArticles.get());
  const activeTab = useSelector(() => ui$.activeTab.get());
  const isShared = useSelector(() => !!listId && listId !== 'all' && isListShared(listId));

  // Hiding shared articles only means anything in the combined All view.
  const showHideShared = pathname === '/all';

  // Archived is a filter here rather than a destination: on a phone it is a
  // narrowing of the same list, and a permanent tab strip cost the list more
  // height than a once-a-session switch earns. Both write `activeTab`, so
  // turning one on turns the other off without extra bookkeeping.
  const selectTab = (tab) => setActiveTab(activeTab === tab ? 'articles' : tab);

  return (
    <Sheet isOpen={isOpen} onClose={onClose} title={t('mobile.viewAndSort')} scrollable>
      <div className="mobile-sheet-section">
        <div className="mobile-sheet-section__label">{t('viewMode.label')}</div>
        <div className="mobile-sheet-section__options" role="group" aria-label={t('viewMode.label')}>
          {VIEW_MODES.map((mode) => (
            <Chip key={mode} grow selected={viewMode === mode} onClick={() => setViewMode(mode)}>
              {t(`viewMode.${mode}`)}
            </Chip>
          ))}
        </div>
      </div>

      <div className="mobile-sheet-section">
        <div className="mobile-sheet-section__label">{t('sort.label')}</div>
        <div className="mobile-sheet-section__options" role="group" aria-label={t('sort.label')}>
          {SORT_OPTIONS.map(({ value, labelKey }) => (
            <Chip key={value} selected={sortBy === value} onClick={() => setSortBy(value)}>
              {t(labelKey)}
            </Chip>
          ))}
        </div>
      </div>

      <div className="mobile-sheet-section">
        <div className="mobile-sheet-section__label">{t('filters.label')}</div>
        <div className="mobile-sheet-section__options" role="group" aria-label={t('filters.label')}>
          <Chip
            selected={showTranslatedOnly}
            onClick={() => setShowTranslatedOnly(!showTranslatedOnly)}
          >
            {t('filters.translatedOnly')}
          </Chip>
          <Chip selected={showAudioOnly} onClick={() => setShowAudioOnly(!showAudioOnly)}>
            {t('filters.audioOnly')}
          </Chip>
          <Chip selected={activeTab === 'archived'} onClick={() => selectTab('archived')}>
            {t('filters.archivedOnly')}
          </Chip>
          {isShared && (
            <Chip selected={activeTab === 'activities'} onClick={() => selectTab('activities')}>
              {t('tabs.activities')}
            </Chip>
          )}
          {showHideShared && (
            <Chip
              selected={hideSharedArticles}
              onClick={() => setHideSharedArticles(!hideSharedArticles)}
            >
              {t('filters.hideShared')}
            </Chip>
          )}
        </div>
      </div>
    </Sheet>
  );
};

export default ViewSortSheet;
